import React from "react";
import { Shield, Phone, ExternalLink } from "lucide-react";
import { getEmergencyInfo } from "../../data/emergencyData";

const LORA = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

function extractDialable(text = "") {
  const match = text.match(/[\+\d][\d\s\-\.\(\)]{3,}/);
  if (!match) return null;
  const digits = match[0].replace(/[\s\-\.\(\)]/g, "");
  return digits.length >= 3 ? digits : null;
}

function NumberCard({ icon, label, value }) {
  if (!value) return null;
  const dialable = extractDialable(value);
  return (
    <div
      className="bg-white border border-[#e5c9c0] px-3 py-2.5 hover:border-[#C17F59] transition-colors"
      style={{ borderRadius: "2px" }}
    >
      <div className="flex items-start gap-2">
        <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-[#C17F59] mb-0.5" style={LORA}>{label}</p>
          {dialable ? (
            <a
              href={`tel:${dialable}`}
              className="text-sm text-[#2B2623] font-medium leading-snug hover:text-[#C17F59] transition-colors"
              style={LORA}
              data-testid={`emergency-tel-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {value}
            </a>
          ) : (
            <p className="text-sm text-[#2B2623] font-medium leading-snug" style={LORA}>{value}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function EmergencyBlock({ urgences, destination, compact = false }) {
  const info = getEmergencyInfo(destination, urgences);

  const numbers = [
    { icon: "🚔", label: "Police", value: info.police },
    { icon: "🔥", label: "Pompiers", value: info.pompiers },
    { icon: "🚑", label: "SAMU / Ambulance", value: info.samu },
    { icon: "🆘", label: "Urgences universelles", value: info.urgences_universelles },
    ...(info.hopital ? [{ icon: "🏥", label: "Hôpital de référence", value: info.hopital }] : []),
  ].filter((n) => n.value);

  const amb = info.ambassade;

  return (
    <div
      className="border border-[#DEC4B8] bg-[#FDF6F3] p-5 space-y-4"
      style={{ borderRadius: "3px" }}
      data-testid="emergency-block"
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <Shield size={16} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0" />
        <h3
          className="text-xl"
          style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}
        >
          Informations utiles & Urgences
        </h3>
      </div>

      {/* Diego's intro */}
      {!compact && (
        <p className="text-sm text-[#5C554E] italic leading-relaxed border-l-2 border-[#C17F59] pl-3" style={LORA}>
          Je vous souhaite un voyage sans encombre, mais parce qu'on ne sait jamais : voici les contacts essentiels sur place en cas d'urgence.
        </p>
      )}

      {/* Numbers grid */}
      {numbers.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={LORA}>
            Numéros d'urgence locaux — Appuyez pour composer
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {numbers.map((n, i) => (
              <NumberCard key={i} {...n} />
            ))}
          </div>
        </div>
      )}

      {/* French Embassy */}
      {amb && (
        <div
          className="bg-white border border-[#DCD4C6] p-4 space-y-2"
          style={{ borderRadius: "2px" }}
          data-testid="embassy-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">🇫🇷</span>
            <p className="text-sm font-semibold text-[#2B2623]" style={SERIF}>{amb.nom}</p>
          </div>
          {amb.adresse && (
            <p className="text-xs text-[#9A8F86] flex items-start gap-1.5" style={LORA}>
              <span className="mt-0.5 flex-shrink-0">📍</span>
              {amb.adresse}
            </p>
          )}
          {(amb.telephone || amb.telephone_urgence) && (
            <div className="flex items-center gap-2">
              <Phone size={11} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0" />
              {amb.telephone ? (
                <a
                  href={`tel:${amb.telephone.replace(/\s/g, "")}`}
                  className="text-sm font-medium text-[#2B2623] hover:text-[#C17F59] transition-colors"
                  style={LORA}
                  data-testid="embassy-phone"
                >
                  {amb.telephone}
                </a>
              ) : (
                <span className="text-sm text-[#5C554E]" style={LORA}>{amb.telephone_urgence}</span>
              )}
            </div>
          )}
          {amb.site && (
            <a
              href={amb.site}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#C17F59] hover:underline"
              style={LORA}
              data-testid="embassy-link"
            >
              <ExternalLink size={11} strokeWidth={1.5} />
              Site consulaire officiel
            </a>
          )}
        </div>
      )}

      <p className="text-xs text-[#9A8F86] italic" style={LORA}>
        Numéros vérifiés à la date de création de votre dossier. En cas de doute, consultez le site du Ministère des Affaires Étrangères.
      </p>
    </div>
  );
}
